import { getISODateTime } from '@liveauctioneers/hammer-ui-core/utils/dateTimeFormat';
import * as React from 'react';
import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router';

import type { CollectionBreadcrumb } from '@/utils/collections/collections.types';

import { PageHeaderMeta } from '@/components/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';
import { getRoundedNumberText } from '@/pages/Search/SearchPage/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getBaseUrl } from '@/redux/modules/config';
import {
    getAllItemSummaries,
    getItemSummaryCatalogId,
    getItemSummaryImageVersion,
    getItemSummaryPhotos,
    getItemSummarySaleStartTs,
    getItemSummarySellerId,
    getItemSummarySellerName,
    getItemSummaryShortDescription,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSearchedAndPromotedItems } from '@/redux/modules/search/aggregateResults/searchAggregate.selectors';
import { getSEOSearchTermData } from '@/redux/modules/search/search.selectors';
import { getSEOSearchPageBreadcrumbs } from '@/redux/modules/searchPromoted.selectors';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { safelyEscapeText } from '@/utils/sanitize';
import { getItemCardImageUrl, logoUrl } from '@/utils/urls';

const DEFAULT_IMAGE_SIZE = 367;

type Props = {
    canonicalURL: string;
    /**
     * A collection page's own breadcrumb trail. Supplied instead of the search API's, which a
     * collection never receives -- it opts out of `seoSearch`, the flag that also gates them.
     */
    collectionBreadcrumbs?: CollectionBreadcrumb[];
    displayLocation: string;
    displaySearchTerm: string;
    /** Collection pages keep their own robots rules; search-party's `indexable` only applies to SEO slugs. */
    isCollectionPage?: boolean;
    /** Collection pages supply their own; omit to keep the search-derived description. */
    metaDescription?: string;
    /** Collection pages supply their own; omit to keep the search-derived title. */
    seoTitle?: string;
    totalFound: number;
};

const getLinkedDataMarkup = (data: any) => {
    return <script type="application/ld+json">{JSON.stringify(data, null, 2)}</script>;
};

const SeoSearchPageHeaderMeta = ({
    canonicalURL,
    collectionBreadcrumbs,
    displayLocation,
    displaySearchTerm,
    isCollectionPage = false,
    metaDescription,
    seoTitle,
    totalFound,
}: Props) => {
    const { pathname } = useLocation();
    const searchPageItemIds = useAppSelector((state) =>
        // Prevent duplicate items from being rendered
        Array.from(new Set(getSearchedAndPromotedItems(state, 1)))
    );
    const breadcrumbs = useAppSelector(getSEOSearchPageBreadcrumbs);
    const seoSearchTerm = useAppSelector(getSEOSearchTermData);
    const baseUrl = useAppSelector(getBaseUrl);

    const firstItemId = searchPageItemIds?.[0];
    const firstItemCatalogId = useAppSelector((state) => getItemSummaryCatalogId(state, firstItemId));
    const firstItemImageVersion = useAppSelector((state) => getItemSummaryImageVersion(state, firstItemId));
    const firstItemPhotos = useAppSelector((state) => getItemSummaryPhotos(state, firstItemId));
    const firstItemSaleStartTs =
        useAppSelector((state) => getItemSummarySaleStartTs(state, firstItemId)) || Date.now() / 1000;
    const firstItemSellerName = useAppSelector((state) => getItemSummarySellerName(state, firstItemId));
    const firstItemSellerId = useAppSelector((state) => getItemSummarySellerId(state, firstItemId));
    const firstItemShortDescription = useAppSelector((state) => getItemSummaryShortDescription(state, firstItemId));
    const firstItemTitle = useAppSelector((state) => getItemSummaryTitle(state, firstItemId));
    const firstPhotoId = firstItemPhotos?.[0] || 0;
    const itemImageUrl =
        getItemCardImageUrl(firstItemSellerId, firstItemCatalogId, firstItemId, firstPhotoId, firstItemImageVersion) ||
        logoUrl;

    // Get all items for structured data and exclude empty items
    const allItems = useAppSelector((state) => Object.values(getAllItemSummaries(state)));

    const { itemCount, totalFoundRounded } = useMemo(() => {
        return {
            itemCount: totalFound,
            totalFoundRounded: getRoundedNumberText({ totalFound }),
        };
    }, [totalFound]);

    // Construct the title with the new format, handling pluralization and zero results.
    // A configured collection title wins outright -- its search term is an internal keyword
    // (e.g. "modern"), so the derived `Bid on "modern"` copy would be wrong.
    let title;
    if (seoTitle) {
        title = `${seoTitle} | LiveAuctioneers`;
    } else if (itemCount === 0) {
        title = `No results found for "${displaySearchTerm}"${displayLocation} | LiveAuctioneers`;
    } else {
        const listingText = itemCount === 1 ? 'auction listing' : 'auction listings';
        title = `Bid on "${displaySearchTerm}"${displayLocation} - ${totalFoundRounded} ${listingText} | LiveAuctioneers`;
    }

    // Add notification count if applicable
    title = useAddNotificationCountToTitle(title);

    const description =
        metaDescription ??
        `Find ${displaySearchTerm} at auction on LiveAuctioneers. Explore unique art, antiques, collectibles, and more from top auction houses. Bid online today!`;

    // Add structured data for all items
    const allItemsLinkedData = React.useMemo(() => {
        if (!allItems.length) {
            return null;
        }

        return allItems.map((item) => {
            const {
                catalogId,
                houseRating,
                houseReviewCount,
                imageVersion,
                itemId,
                leadingBid,
                photos,
                sellerId,
                sellerName,
                shortDescription,
                startPrice,
                title: itemTitle,
            } = item;

            const firstPhotoId = photos?.[0] || 0;
            const itemImageUrl =
                getItemCardImageUrl(sellerId, catalogId, itemId, firstPhotoId, imageVersion) || logoUrl;

            return {
                '@context': 'https://schema.org',
                '@type': 'Product',
                description: safelyEscapeText(shortDescription),
                image: itemImageUrl,
                name: safelyEscapeText(itemTitle),
                offers: {
                    '@type': 'Offer',
                    price: (leadingBid || startPrice || 0).toString(),
                    priceCurrency: 'USD',
                    seller: {
                        '@type': 'Organization',
                        name: sellerName,
                    },
                    url: canonicalURL,
                },
                sku: itemId?.toString(),
                // Don't markup items without reviews
                ...(houseReviewCount > 0
                    ? {
                          organization: {
                              '@context': 'https://schema.org',
                              '@type': 'Organization',
                              aggregateRating: {
                                  '@type': 'AggregateRating',
                                  bestRating: 5,
                                  ratingValue: houseRating?.toFixed(1),
                                  reviewCount: houseReviewCount,
                                  worstRating: 1,
                              },
                              name: sellerName,
                              review: [
                                  {
                                      '@type': 'Review',
                                      author: {
                                          '@type': 'Organization',
                                          name: 'LiveAuctioneers',
                                      },
                                      reviewRating: {
                                          '@type': 'Rating',
                                          bestRating: 5,
                                          ratingValue: houseRating?.toFixed(1),
                                          worstRating: 1,
                                      },
                                  },
                              ],
                              url: `${baseUrl}/seller/${sellerId}`,
                          },
                      }
                    : {}),
            };
        });
    }, [allItems, canonicalURL, baseUrl]);

    const imageLinkedData = React.useMemo(() => {
        if (!firstItemId) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'ImageObject',
            author: {
                '@type': 'Organization',
                name: 'LiveAuctioneers',
                url: 'https://www.liveauctioneers.com',
            },
            contentUrl: itemImageUrl,
            copyrightHolder: 'LiveAuctioneers',
            copyrightNotice: 'LiveAuctioneers',
            creator: {
                '@type': 'Organization',
                name: 'LiveAuctioneers',
                url: 'https://www.liveauctioneers.com',
            },
            creditText: escapeQuotes(firstItemSellerName || 'LiveAuctioneers'),
            description: escapeQuotes(safelyEscapeText(firstItemShortDescription)),
            fileFormat: 'image/png',
            height: DEFAULT_IMAGE_SIZE,
            mainEntityOfPage: canonicalURL,
            name: escapeQuotes(safelyEscapeText(firstItemTitle)) + ' main image',
            representativeOfPage: true,
            sourceOrganization: 'LiveAuctioneers',
            thumbnailUrl: itemImageUrl,
            uploadDate: getISODateTime(new Date(firstItemSaleStartTs * 1000), { short: true }),
            URL: itemImageUrl,
            width: DEFAULT_IMAGE_SIZE,
        };
    }, [
        firstItemId,
        itemImageUrl,
        firstItemSellerName,
        firstItemShortDescription,
        canonicalURL,
        firstItemTitle,
        firstItemSaleStartTs,
    ]);

    const breadcrumbLinkedData = React.useMemo(() => {
        // Start with the implicit "Home" breadcrumb
        const itemList = [
            {
                '@type': 'ListItem',
                item: {
                    '@id': baseUrl,
                    name: 'Home',
                },
                position: 1,
            },
            /*
             * A collection page's trail comes from its own config. It never has API breadcrumbs to
             * fall back on -- those ride on `seoSearch`, which a collection opts out of because an
             * unregistered search term 404s the whole request (LAAC-2380) -- and config is the more
             * accurate source anyway: the API's trail for /collections/20th-century-design/ reads
             * "Home > Modern", keyed off that page's internal search keyword, while config and the
             * visible on-page trail both read "Home > Furniture > 20th Century Design".
             */
            ...(collectionBreadcrumbs ?? []).map((breadcrumb, index) => ({
                '@type': 'ListItem',
                item: {
                    // Empty href marks the current page, exactly as the API's own trail does.
                    '@id': `${baseUrl}${breadcrumb.href || pathname}`,
                    name: safelyEscapeText(breadcrumb.label),
                },
                position: index + 2, // Start position from 2
            })),
            // Add breadcrumbs from the selector
            ...(collectionBreadcrumbs
                ? []
                : breadcrumbs.map((breadcrumb, index) => ({
                      '@type': 'ListItem',
                      item: {
                          // If the breadcrumb URL is not available, use the current pathname
                          '@id': `${baseUrl}${breadcrumb.url ? breadcrumb.url : pathname}`,
                          name: safelyEscapeText(breadcrumb.name),
                      },
                      position: index + 2, // Start position from 2
                  }))),
        ];

        if (itemList.length <= 1) {
            // Don't render breadcrumb linked data if only "Home" exists
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: itemList,
        };
    }, [baseUrl, breadcrumbs, collectionBreadcrumbs, pathname]);

    // seo-dbi marks thin slugs `indexable: false`; legacy SEO terms never send it. Collection pages are
    // excluded explicitly so their robots never depend on what search-party returns for the keyword.
    const isThinSeoSlug = !isCollectionPage && seoSearchTerm?.indexable === false;
    const robotsContent = itemCount === 0 || isThinSeoSlug ? 'noindex, follow' : 'index, follow';

    return (
        <PageHeaderMeta
            canonical={canonicalURL}
            description={description}
            imageUrl={itemImageUrl}
            title={title}
        >
            <meta
                content={robotsContent}
                name="robots"
            />
            <meta
                content={`${DEFAULT_IMAGE_SIZE}`}
                property="og:image:height"
            />
            <meta
                content={`${DEFAULT_IMAGE_SIZE}`}
                property="og:image:width"
            />
            <meta
                content="LiveAuctioneers"
                property="og:site_name"
            />
            {/* Add product structured data for each item as a list item */}
            {allItemsLinkedData &&
                getLinkedDataMarkup({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    itemListElement: allItemsLinkedData.map((item, index) => ({
                        '@type': 'ListItem',
                        item,
                        position: index + 1,
                    })),
                })}
            {imageLinkedData && getLinkedDataMarkup(imageLinkedData)}
            {breadcrumbLinkedData && getLinkedDataMarkup(breadcrumbLinkedData)}
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(SeoSearchPageHeaderMeta, isEqual);
